import React, { useMemo } from 'react';
import { useController } from 'react-hook-form';

import FacilityTreeBlock from '@components/common/FacilityTreeBlock/FacilityTreeBlock';
import Tabs from '@components/uikit/Tabs/Tabs';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { USER_FIELDS } from '../../constants/schemaUser';
import { addDisabledField } from '../../helpers/addDisabledField';
import { useSubscriptionsUserActions } from '../../store/useSubscriptionsUserStore';
import style from './style.module.scss';

const StepFacilities = ({ control }) => {
  const { t } = useTranslation();

  const { data: profileData } = useUserProfileQuery();
  const { data: assetTreeData } = useAssetTreeQuery();

  const { setUserLocationList } = useSubscriptionsUserActions();

  const {
    field: { value: allFacilitiesValue, onChange: allFacilitiesChange },
  } = useController({ name: USER_FIELDS.ALL_FACILITIES, control });

  const {
    field: { onChange: locationsChange },
  } = useController({
    control,
    name: USER_FIELDS.LOCATIONS,
  });

  const facilityStructureData = assetTreeData?.facilities;

  const processedFacilityStructureData = useMemo(
    () => addDisabledField(facilityStructureData),
    [facilityStructureData],
  );

  const toggleLocationItems = [
    { value: 1, label: t('All Locations') },
    { value: 0, label: t('Select Locations') },
  ];

  const handleTabsChange = (value) => {
    if (allFacilitiesValue === value) {
      return;
    }

    allFacilitiesChange(value);

    if (value) {
      const facilitiesIds = [];
      const facilities = assetTreeData?.facilities.map((facility) => {
        facilitiesIds.push(facility.id);

        return { ...facility, children: [] };
      });

      locationsChange(facilitiesIds);
      setUserLocationList(facilities);
    } else {
      locationsChange([]);
      setUserLocationList([]);
    }
  };

  const variant = profileData?.user?.all_facilities
    ? allFacilitiesValue
      ? 'stepAllFacilities'
      : 'stepFacilitiesWithTabs'
    : 'stepFacilities';

  return (
    <>
      <div className={style.facilitiesSwitchWrapper}>
        <div className={style.facilitiesSwitch}>
          {profileData?.userAccess?.manageData && !!profileData?.user?.all_facilities && (
            <>
              <Tabs
                value={allFacilitiesValue}
                tabsItems={toggleLocationItems}
                label={t('Select Facility/Locations')}
                type={'woHelperText'}
                onChange={handleTabsChange}
              />
              <span className={style.facilitiesSwitchSubtext}>{t('including all future facilities')}</span>
            </>
          )}
        </div>
        {!!allFacilitiesValue && (
          <div className="checked-list-text">
            {t(
              'User can access all existing facilities and any new facilities that are created in the future.',
            )}
          </div>
        )}
      </div>
      <FacilityTreeBlock
        control={control}
        locationsFormName={USER_FIELDS.LOCATIONS}
        facilityStructureData={processedFacilityStructureData}
        variant={variant}
        disabled={allFacilitiesValue && !!profileData?.user?.all_facilities}
        showAllFacilitiesBlock={allFacilitiesValue === 1}
        showSearch={allFacilitiesValue === 0}
        showSelectAll={allFacilitiesValue === 0}
      />
    </>
  );
};

export default StepFacilities;
